import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState('');
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const defaultContactInfo = {
    pageHeading: 'Contact Us',
    pageSubtitle: "We're here to help with any questions or comments",
    email: 'ebusiness.contact@gmail.com',
    phone: '+8809647001250',
    address: 'Aftabnagar, Dhaka, Bangladesh',
    googleMapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.8971203935644!2d90.42902931498165!3d23.753056884589!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b85c740f17d1%3A0x8c3f9ba2dbfba9a8!2sAftabnagar%2C%20Dhaka!5e0!3m2!1sen!2sbd!4v1234567890123!5m2!1sen!2sbd',
    googleMapSearchQuery: 'Aftabnagar,Dhaka,Bangladesh',
    companyName: 'E-Business',
    companyDescription: "Your trusted online shopping platform. We are committed to providing high-quality products and the best customer service. Don't hesitate to contact us for any questions or assistance."
  };

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/api/contact-info');
        if (response.data.success) {
          setContactInfo(response.data.contactInfo);
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError('Failed to load contact info');
        setContactInfo(defaultContactInfo);
      } finally {
        setLoading(false);
      }
    };
    fetchContactInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    const apiUrl = 'https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/contact';
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setFormStatus('error');
    }
  };

  const appleFont = {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
  };

  if (loading) {
    return (
      <div style={appleFont} className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-400 tracking-wide">Loading...</p>
        </div>
      </div>
    );
  }

  const info = contactInfo || defaultContactInfo;

  return (
    <div style={appleFont} className="min-h-screen bg-white py-16 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto border border-gray-200 rounded-2xl shadow-sm overflow-hidden">

        {/* Hero Header */}
        <div className="px-10 pt-12 pb-10 border-b border-gray-100">
          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">Get in Touch</p>
          <h1 className="text-4xl font-semibold text-gray-900 tracking-tight mb-2">{info.pageHeading}</h1>
          <p className="text-base text-gray-500">{info.pageSubtitle}</p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
          {[
            {
              icon: (
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              ),
              label: 'Email',
              content: <a href={`mailto:${info.email}`} className="text-sm text-gray-700 hover:text-gray-900 underline underline-offset-2">{info.email}</a>
            },
            {
              icon: (
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.774a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              ),
              label: 'Phone',
              content: <a href={`tel:${info.phone}`} className="text-sm text-gray-700 hover:text-gray-900 underline underline-offset-2">{info.phone}</a>
            },
            {
              icon: (
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              ),
              label: 'Address',
              content: <p className="text-sm text-gray-700">{info.address}</p>
            },
          ].map((item, i) => (
            <div key={i} className="px-6 py-6 flex flex-col gap-3">
              {item.icon}
              <div>
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-1">{item.label}</p>
                {item.content}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Form */}
        <div className="px-10 py-10 border-b border-gray-100">
          <h2 className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-8">Send a Message</h2>

          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your name"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="example@email.com"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="Enter subject"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">Your Message</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Write your message here..."
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none"
              />
            </div>

            <div>
              <button
                onClick={handleSubmit}
                disabled={formStatus === 'submitting'}
                className="w-full bg-gray-900 hover:bg-gray-800 active:bg-black text-white font-medium py-3 px-6 rounded-lg text-sm tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </div>

            {formStatus === 'success' && (
              <div className="bg-green-50 border border-green-100 text-green-700 px-4 py-3 rounded-lg text-sm">
                <strong>Success!</strong> Your message has been sent. We'll get back to you soon.
              </div>
            )}
            {formStatus === 'error' && (
              <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-lg text-sm">
                <strong>Error!</strong> Failed to send message. Please try again.
              </div>
            )}
          </div>
        </div>

        {/* Google Map */}
        <div className="border-b border-gray-100">
          <div className="px-10 py-5 border-b border-gray-100">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-0.5">Find Us</p>
            <p className="text-sm text-gray-600 mt-1">{info.address}</p>
          </div>
          <div className="h-72 w-full">
            <iframe
              src={info.googleMapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Location"
            />
          </div>
          <div className="px-10 py-4">
            
           <a   href={`https://www.google.com/maps/search/?api=1&query=${info.googleMapSearchQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
           >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              View on Google Maps
            </a>
          </div>
        </div>

        {/* Footer Note */}
        <div className="px-10 py-8 text-center">
          <p className="text-sm font-medium text-gray-900 mb-1">{info.companyName}</p>
          <p className="text-sm text-gray-400 leading-relaxed max-w-xl mx-auto">{info.companyDescription}</p>
        </div>

      </div>
    </div>
  );
};

export default ContactUsPage;